import { lazy, Suspense, useCallback, useEffect, useId, useRef, useState } from "react";
import logo from "../../../packages/design/src/brand/spikeeasy-bv-academy.svg";
import { api, hasRole, type Account, type Session } from "./api";
import { forgetActors } from "./offline";
import { TournamentApp } from "./App";
import { Admin } from "./Admin";
import { Coaching } from "./Coaching";
import { UploadLink } from "./UploadLink";
import { Profile } from "./Profile";
import { EmailSignIn, SignIn } from "./SignIn";
import { Link } from "./Link";
import { safeNext } from "./safe-next";
import { Notice } from "./ui";

interface Place {
  path: string;
  search: string;
}
// The review editor and viewer are large and only needed on their own pages.
const CoachReview = lazy(() => import("./CoachReview").then((m) => ({ default: m.CoachReview })));
const MyReview = lazy(() => import("./MyReview").then((m) => ({ default: m.MyReview })));

const here = (): Place => ({ path: location.pathname, search: location.search });
const offline: Session = { actor: null, account: null };

function destinations(account: Account | null) {
  if (!account) return [];
  return [
    { href: "/", label: "Tournaments" },
    { href: "/me", label: "Profile" },
    ...(hasRole(account, "coach") || hasRole(account, "admin")
      ? [{ href: "/coach", label: "Video reviews" }]
      : []),
    ...(hasRole(account, "admin") ? [{ href: "/admin", label: "People" }] : []),
  ];
}

export function Shell() {
  const [place, setPlace] = useState(here);
  const [session, setSession] = useState<Session | null>(null);
  const [generation, setGeneration] = useState(0);
  const go = useCallback((href: string) => {
    const url = new URL(href, location.origin);
    if (url.origin !== location.origin) return;
    history.pushState({}, "", `${url.pathname}${url.search}`);
    setPlace({ path: url.pathname, search: url.search });
    window.scrollTo(0, 0);
  }, []);
  const loadSession = useCallback(async () => {
    try {
      setSession(await api<Session>("/session"));
    } catch {
      setSession((current) => current ?? offline);
    }
  }, []);
  useEffect(() => {
    void loadSession();
    const pop = () => setPlace(here());
    window.addEventListener("popstate", pop);
    return () => window.removeEventListener("popstate", pop);
  }, [loadSession]);
  const account = session?.account ?? null;
  const signedIn = !!account || session?.actor?.role === "team";
  const signOut = async (everywhere = false) => {
    if (everywhere) await api("/me/sign-out-everywhere", {});
    else await api("/logout", {}).catch(() => {});
    await forgetActors().catch(() => {});
    setSession((current) => ({ ...(current ?? offline), actor: null, account: null }));
    setGeneration((n) => n + 1);
    go("/");
  };
  const signedInAt = async (next: string) => {
    await loadSession();
    setGeneration((n) => n + 1);
    go(safeNext(next));
  };
  const { path, search } = place;
  const params = new URLSearchParams(search);
  const nextHere = encodeURIComponent(`${path}${search}`);

  let content;
  if (path === "/sign-in")
    content = (
      <SignIn
        next={safeNext(params.get("next"))}
        account={account}
        go={go}
        turnstileSiteKey={session?.turnstileSiteKey ?? null}
      />
    );
  else if (path === "/sign-in/email")
    content = <EmailSignIn go={go} signedIn={signedInAt} />;
  else if (path === "/upload") content = <UploadLink go={go} signedIn={signedInAt} />;
  else if (["/me", "/admin", "/coach"].some((root) => path === root || path.startsWith(`${root}/`))) {
    const root = path.split("/")[1];
    const myReview = path.match(/^\/me\/reviews\/([a-zA-Z0-9_-]+)$/)?.[1];
    const coachReview = path.match(/^\/coach\/reviews\/([a-zA-Z0-9_-]+)$/)?.[1];
    const allowed =
      root === "me"
        ? !!account
        : root === "admin"
          ? hasRole(account, "admin")
          : hasRole(account, "coach") || hasRole(account, "admin");
    content = !session ? (
      <main id="main" className="app-main">
        <Notice>Loading…</Notice>
      </main>
    ) : !allowed ? (
      <NoAccess account={account} go={go} next={nextHere} />
    ) : myReview ? (
      <MyReview key={myReview} id={myReview} account={account!} go={go} />
    ) : coachReview ? (
      <CoachReview key={coachReview} id={coachReview} account={account!} go={go} />
    ) : path === "/me" ? (
      <Profile
        key={generation}
        account={account!}
        go={go}
        accountChanged={loadSession}
        signOutEverywhere={() => signOut(true)}
      />
    ) : path === "/admin" ? (
      <Admin account={account!} go={go} />
    ) : path === "/coach" ? (
      <Coaching go={go} />
    ) : (
      <NotFound go={go} />
    );
  } else
    content = (
      <TournamentApp key={generation} path={path} go={go} account={account} />
    );

  return (
    <>
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <header className="app-header">
        <Link go={go} href="/" aria-label="Spike Easy home">
          <img src={logo} alt="Spike Easy Beach Volleyball Academy" />
        </Link>
        {session &&
          (signedIn ? (
            <AccountMenu
              key={path}
              account={account}
              path={path}
              go={go}
              signOut={signOut}
            />
          ) : (
            path !== "/sign-in" && (
              <Link go={go} className="secondary" href={`/sign-in?next=${nextHere}`}>
                Sign in
              </Link>
            )
          ))}
      </header>
      <Suspense
        fallback={
          <main id="main" className="app-main">
            <Notice>Loading…</Notice>
          </main>
        }
      >
        {content}
      </Suspense>
    </>
  );
}

function AccountMenu({
  account,
  path,
  go,
  signOut,
}: {
  account: Account | null;
  path: string;
  go: (href: string) => void;
  signOut: () => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const toggle = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent | KeyboardEvent) => {
      if (event instanceof KeyboardEvent) {
        if (event.key !== "Escape") return;
        setOpen(false);
        toggle.current?.focus();
      } else {
        const target = event.target as Node;
        if (!panel.current?.contains(target) && !toggle.current?.contains(target)) setOpen(false);
      }
    };
    document.addEventListener("keydown", close);
    document.addEventListener("click", close);
    return () => {
      document.removeEventListener("keydown", close);
      document.removeEventListener("click", close);
    };
  }, [open]);
  const links = destinations(account);
  const label = account ? account.name ?? account.email : "Team scoring";
  return (
    <nav className="account-nav" aria-label="App">
      <button
        ref={toggle}
        type="button"
        className="account-toggle"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="account-toggle-label">Menu</span>
      </button>
      <div ref={panel} id={menuId} className="account-panel" data-open={open || undefined}>
        <p className="account-name">{label}</p>
        <ul>
          {links.map((link) => (
            <li key={link.href}>
              <Link
                go={go}
                href={link.href}
                aria-current={
                  (link.href === "/" ? path === "/" || path.startsWith("/t/") : path === link.href || path.startsWith(`${link.href}/`))
                    ? "page"
                    : undefined
                }
                onClick={() => setOpen(false)}
              >
                {link.label}
              </Link>
            </li>
          ))}
          <li>
            <button type="button" className="text-button" onClick={() => void signOut()}>
              Sign out
            </button>
          </li>
        </ul>
      </div>
    </nav>
  );
}

function NotFound({ go }: { go: (href: string) => void }) {
  return (
    <main id="main" className="app-main">
      <section className="empty-state">
        <h1>This page doesn't exist.</h1>
        <Link go={go} href="/">
          Back to tournaments
        </Link>
      </section>
    </main>
  );
}

function NoAccess({
  account,
  go,
  next,
}: {
  account: Account | null;
  go: (href: string) => void;
  next: string;
}) {
  return (
    <main id="main" className="app-main">
      <section className="empty-state">
        {account ? (
          <>
            <h1>This page isn't part of your account.</h1>
            <p>
              You're signed in as {account.email}. If you should have access,
              ask a Spike Easy admin to add the role.
            </p>
            <Link go={go} href="/">
              Back to tournaments
            </Link>
          </>
        ) : (
          <>
            <h1>Sign in to open this page.</h1>
            <Link go={go} className="primary" href={`/sign-in?next=${next}`}>
              Sign in
            </Link>
          </>
        )}
      </section>
    </main>
  );
}
